import { useAeropayUser } from '@iheartjane/data-access/react-query';
import { JaneGoldCard, OhioGoldEducation } from '@iheartjane/menu';
import { Flex, JaneGoldIcon, Typography } from '@iheartjane/reefer';
import type { KioskUser } from '@iheartjane/types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { getJwt } from '@jane/shared/auth';
/* eslint-disable-next-line no-restricted-imports */
import type { BrandSpecial } from '@jane/shared/models';

import { useBrandSpecials } from '../../../providers';
import { trackCashBackDetailPageTap } from '../../../util/lib/tracking/track';
import { LearnMoreLink } from '../LearnMoreLink/LearnMoreLink';
import { SpecialsGrid } from '../SpecialsGrid/SpecialsGrid';

export interface BrandSpecialRowProps {
  defaultLength?: number;
  isFeaturedPage?: boolean;
  kioskUser?: KioskUser | null;
  storeId: number;
  storeState?: string | null;
}

export const BrandSpecialsRow = ({
  defaultLength = 6,
  isFeaturedPage = false,
  kioskUser,
  storeId,
  storeState,
}: BrandSpecialRowProps) => {
  const isOhioStore = storeState === 'Ohio';
  const navigate = useNavigate();
  const jwt = getJwt();
  const [showViewMore, setShowViewMore] = useState(false);

  const { brandSpecials, loadingBrandSpecials } = useBrandSpecials();
  const visibleBrandSpecials = showViewMore
    ? brandSpecials.slice(0, defaultLength)
    : brandSpecials;
  const { data: aeropayUser } = useAeropayUser({
    janeUserId: isOhioStore ? kioskUser?.id || null : null,
    jwt,
  });
  const userHasLinkedBank = !!aeropayUser?.bank_accounts?.length;

  const handleViewMoreClick = () => {
    if (isFeaturedPage) {
      navigate(`/stores/${storeId}/menu/jane-gold`);
    } else {
      setShowViewMore(false);
    }
  };

  useEffect(() => {
    if (!loadingBrandSpecials) {
      setShowViewMore(isFeaturedPage || brandSpecials.length > defaultLength);
    }
  }, [brandSpecials, defaultLength, isFeaturedPage, loadingBrandSpecials]);

  if (!loadingBrandSpecials && (!brandSpecials || brandSpecials.length === 0)) {
    return null;
  }

  return (
    <SpecialsGrid
      title={
        <Flex flexDirection="column">
          <Flex flexDirection="row" gap={8} alignItems="center">
            <JaneGoldIcon size="md" />
            <Typography variant="header-bold">Jane Gold Rewards</Typography>
          </Flex>
          {isOhioStore && (
            <OhioGoldEducation
              learnMoreLink={
                <LearnMoreLink userHasLinkedBank={userHasLinkedBank} />
              }
            />
          )}
        </Flex>
      }
      showViewMore={showViewMore}
      onViewMoreClick={handleViewMoreClick}
      showViewLess={!showViewMore && brandSpecials.length > defaultLength}
      onViewLessClick={() => setShowViewMore(true)}
      isLoading={loadingBrandSpecials}
    >
      {visibleBrandSpecials?.map((special: BrandSpecial, index) => {
        const {
          discount_label,
          id,
          title,
          photo,
          product_brand: { name: brandName, logo_url: partnerLogo },
          rules,
          special_type,
        } = special;

        const handleBrandSpecialClick = () => {
          trackCashBackDetailPageTap({
            brandSpecial: special,
            source: [isFeaturedPage ? 'Featured' : 'Jane Gold'],
            rank: index + 1, // accounts for zero leading
          });
          navigate(`/stores/${storeId}/menu/jane-gold/${id}`, {
            state: {
              menuCategory: isFeaturedPage ? 'featured' : 'jane-gold',
            },
          });
        };

        return (
          <JaneGoldCard
            authenticated={!!kioskUser}
            brandImage={partnerLogo}
            brandName={brandName}
            imageSrc={photo?.urls.original}
            isBundle={special_type !== 'product'}
            key={id}
            onClick={handleBrandSpecialClick}
            rules={rules}
            specialType={special_type}
            tagLabel={discount_label}
            title={title}
          />
        );
      })}
    </SpecialsGrid>
  );
};
